import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { API_URL } from "../lib/supabase";
import { useCountry } from "../contexts/CountryContext";
import { MapPin, Star, ChevronRight, User, ShieldCheck, ArrowRight } from "lucide-react";
import { ProviderBadges, StripeSecuredBadge } from "../components/shared/ProviderBadges";
import { useAuth } from "../contexts/AuthContext";
import { formatDateNZ } from "../lib/format";

const ProviderDetailPage = () => {
  const { id } = useParams();
  const { profile } = useAuth();
  const { countryCode } = useCountry();
  const [provider, setProvider] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProvider();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, countryCode]);

  const fetchProvider = async () => {
    setLoading(true);
    try {
      // Country scoping: backend 404s if the provider's country_code
      // doesn't match the active country (host in prod, toggle in preview).
      const response = await fetch(`${API_URL}/providers/${id}?country_code=${encodeURIComponent(countryCode)}`);
      if (!response.ok) {
        setProvider(null);
        return;
      }
      const data = await response.json();
      setProvider(data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="loading">Loading...</div>;
  if (!provider) return <div className="error">Provider not found</div>;

  // Owner-only "Get Verified" prompt: visible to the logged-in provider on
  // their OWN public profile, only when ID is not yet verified.
  const isOwner = profile?.id && provider.profile_id && profile.id === provider.profile_id;
  const showGetVerifiedPrompt = isOwner && !provider.is_id_verified;

  return (
    <div className="page provider-detail-page">
      <div className="breadcrumb">
        <Link to="/providers">Providers</Link>
        <ChevronRight size={16} />
        <span>{provider.business_name}</span>
      </div>

      <div className="provider-detail-header">
        <div className="provider-avatar xlarge">
          {provider.profile?.avatar_url ? (
            <img src={provider.profile.avatar_url} alt={provider.business_name} />
          ) : (
            <User size={60} />
          )}
        </div>
        <div className="provider-header-info">
          <h1>{provider.business_name}</h1>
          <div className="provider-rating large">
            <Star size={20} fill="currentColor" />
            <span>{provider.average_rating?.toFixed(1) || 'New'}</span>
            <span className="review-count">({provider.review_count} reviews)</span>
          </div>
          <ProviderBadges provider={provider} size="md" showCarefullyReviewed />

          {showGetVerifiedPrompt && (
            <div className="get-verified-prompt" data-testid="get-verified-prompt">
              <div className="get-verified-icon">
                <ShieldCheck size={20} strokeWidth={2.2} />
              </div>
              <div className="get-verified-body">
                <div className="get-verified-title">Get Verified <span className="get-verified-pill">Free</span></div>
                <p className="get-verified-message">
                  Verify your profile to earn the Verified badge and build more trust with customers.
                </p>
                <p className="get-verified-sub">
                  Verification is quick and helps customers choose you with confidence.
                </p>
              </div>
              <Link
                to="/provider/documents"
                className="get-verified-cta"
                data-testid="get-verified-cta"
              >
                Upload Documents <ArrowRight size={14} />
              </Link>
            </div>
          )}
        </div>
      </div>

      <div className="provider-detail-grid">
        <div className="provider-detail-main">
          <section className="provider-section">
            <h2>About</h2>
            <p>{provider.bio || 'No description provided.'}</p>
          </section>

          <section className="provider-section">
            <h2>Services Offered</h2>
            <div className="services-list">
              {provider.services?.map(service => (
                <Link 
                  to={`/services/${service.slug}`} 
                  key={service.id} 
                  className="service-list-item"
                >
                  <span>{service.name}</span>
                  <span className="price">{service.price_display}</span>
                </Link>
              ))}
            </div>
          </section>

          <section className="provider-section">
            <h2>Reviews</h2>
            {provider.reviews?.length > 0 ? (
              <div className="reviews-list">
                {provider.reviews.map(review => (
                  <div key={review.id} className="review-card">
                    <div className="review-header">
                      <div className="review-rating">
                        {[...Array(5)].map((_, i) => (
                          <Star 
                            key={i} 
                            size={14} 
                            fill={i < review.rating ? 'currentColor' : 'none'}
                          />
                        ))}
                      </div>
                      <span className="review-date">
                        {formatDateNZ(review.created_at)}
                      </span>
                    </div>
                    <p>{review.comment}</p>
                    <div className="review-author">
                      — {review.customer?.full_name || 'Anonymous'}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="no-reviews">No reviews yet.</p>
            )}
          </section>
        </div>

        <div className="provider-detail-sidebar">
          <div className="provider-contact-card">
            <h3>Get in Touch</h3>
            <div className="contact-item">
              <MapPin size={18} />
              <span>{provider.city || 'Location not specified'}</span>
            </div>

            <Link
              to={`/quote-request/${provider.services?.[0]?.slug || 'other'}`}
              className="btn-primary btn-large"
              style={{ marginTop: '16px', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
              data-testid="request-quote-btn"
            >
              Request a Quote <ArrowRight size={16} />
            </Link>

            <div className="provider-stats-card" style={{ marginTop: '20px' }}>
              <div className="stat">
                <span className="stat-value">{provider.years_experience || 0}</span>
                <span className="stat-label">Years Experience</span>
              </div>
              <div className="stat">
                <span className="stat-value">{provider.jobs_completed}</span>
                <span className="stat-label">Jobs Completed</span>
              </div>
              {provider.hourly_rate_min && (
                <div className="stat">
                  <span className="stat-value">${provider.hourly_rate_min}</span>
                  <span className="stat-label">Starting Rate/hr</span>
                </div>
              )}
            </div>
            <div style={{ marginTop: '14px', paddingTop: '14px', borderTop: '1px solid var(--border-color)', display: 'flex', justifyContent: 'center' }}>
              <StripeSecuredBadge />
            </div>
          </div>

          <div
            data-testid="provider-disclaimer"
            style={{ marginTop: '16px', padding: '14px 16px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 'var(--radius-md)' }}
          >
            <p style={{ margin: 0, fontSize: '0.8125rem', lineHeight: 1.55, color: '#92400e', fontStyle: 'italic' }}>
              HandyHub reviews provider information during onboarding, however customers should independently assess providers before engaging services.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};


export default ProviderDetailPage;
